// MinhaLista.js — Seção "Minha Lista" (perfil): livros salvos para comprar depois.
// Usa a wishlist do CarrinhoContext (persistida em localStorage).
//
// ================= NO MOLDE DA ABA E-BOOKS (11/09/2026) =================
//
// Era uma lista de linhas da largura do perfil inteiro: capa de 48px à
// esquerda, um botão "Adicionar" de 160px na ponta direita e, entre os dois,
// uns 500px de nada. Dois livros ocupavam meia tela. O dono pediu o padrão que
// o site já tem nas outras abas, e economizando espaço — então ela ganhou o
// que a aba E-books tem:
//
//  - CARDS COMPACTOS EM GRADE (1, 2 e 3 por linha), com a capa, o texto e as
//    ações dentro do mesmo cartão. Doze por página fecha as três larguras
//    sem fileira pela metade;
//  - A BUSCA QUE ACOMPANHA A ROLAGEM e some quando o cabeçalho do site volta
//    (useBarraGrudada, a mesma regra das outras barras) — só a partir de sete
//    livros: com três, uma barra de busca é mobília;
//  - "NO CARRINHO" no lugar de "Adicionar" quando o livro já está lá. Antes o
//    botão aceitava o segundo clique e abria o aviso de "adicionado" de novo,
//    sem mudar nada no carrinho.
//
// O resumo do topo soma os preços SALVOS. É o preço de quando o coração foi
// clicado, e o checkout confere o de agora (PagarmeController) — por isso o
// texto diz "somam", e não "custam".
//
// ================= SEGUNDA PASSAGEM: OS DETALHES (11/09/2026) =================
//
// A grade ficou; o que estava feio era o acabamento. Só o card tinha QUATRO
// receitas de canto e de altura escritas à mão (capa, botão de adicionar, botão
// de tirar e o chip "No carrinho"), e a busca era a quarta variação de <input>
// do site. O que mudou, e por quê:
//
//  - DUAS AÇÕES, uma sólida e uma de contorno, declaradas logo abaixo. Num card
//    que se repete doze vezes por página é onde a divergência de 2px aparece;
//  - CAPA DE 80x120 no lugar de 64x96, e as ações ocupando a largura do texto
//    em vez de se espremerem na ponta direita. Com a capa pequena sobrava um vão
//    entre o preço e os botões — o card ainda estava montado para a linha da
//    largura do perfil, que é justamente o que ele deixou de ser;
//  - O CAMPO DE BUSCA É O DO SITE (campoCls). A lupa é a única adaptação;
//  - O RESUMO E OS DOIS ESTADOS VAZIOS viraram superfície (.bloco). Texto solto
//    no meio do nada não se lê como resposta da tela; se lê como falta.
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CarrinhoContext } from "../../../services/context/CarrinhoContext";
import { useModal } from "../../../services/context/ModalContext";
import NotaLivro from "../../produto/paginaProduto/NotaLivro";
import { campoCls } from "../../ui/Formulario";
import Pagination from "../../utils/Pagination";
import { message } from "../../utils/toast";
import useBarraGrudada, { CLASSES_ESCONDIDA, CLASSES_SUMICO } from "../../../hooks/useBarraGrudada";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

const POR_PAGINA = 12;
// A partir de quantos livros a busca aparece (ver o cabeçalho).
const BUSCA_A_PARTIR_DE = 7;

// ===================== AS DUAS AÇÕES DESTA TELA =====================
//
// São duas, e não mais do que duas: COMPRAR (sólida, laranja) e TIRAR DA LISTA
// (contorno). Moram aqui em cima porque o card se repete doze vezes por página
// — é exatamente onde um botão com altura própria e outro com canto próprio
// viram uma fileira desalinhada.
//
// A ALTURA É 40px, e não os 32px de antes: o botão de tirar fica encostado no
// de adicionar, e num alvo de 32px o toque errado apaga o livro da lista. O vão
// de 10px entre os dois é parte da mesma conta.
//
// A COR DA BORDA NÃO ESTÁ AQUI, de propósito: o Bootstrap declara `.border` com
// !important (ver o App.css), então qualquer borda do site sai na hairline do
// tema por mais que o Tailwind peça outra coisa. Quem diz o estado é o fundo
// tonalizado e a cor do texto — é assim que "No carrinho" fica laranja usando a
// MESMA receita de contorno do botão de tirar.
const ACAO_SOLIDA =
  "btn-solido foco-laranja inline-flex h-10 items-center justify-center gap-1.5 rounded-controle " +
  "px-3.5 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-50";
const ACAO_CONTORNO =
  "foco-laranja inline-flex h-10 items-center justify-center gap-1.5 rounded-controle border border-line " +
  "px-3.5 text-xs font-bold transition-colors";

const precoDe = (item) => parseFloat(item.preco) || 0;
const fmtPreco = (v) => v.toFixed(2).replace(".", ",");
const autorDe = (item) => {
  const a = item.autores;
  if (Array.isArray(a) && a.length) return a.map((x) => (typeof x === "object" ? x?.nome : x)).filter(Boolean).join(", ");
  return item.autor || "";
};
// Listas antigas guardaram o tamanho do impresso em `formato` em vez de
// "ebook"/"fisico" (ver CarrinhoContext). Só "fisico" é impresso de verdade.
const formatoDe = (item) => (item.formato === "fisico" ? "fisico" : "ebook");
const semAcento = (s) =>
  String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const MinhaLista = () => {
  const { listaDesejos, removerDesejo, adicionarItem, carrinho } = useContext(CarrinhoContext);
  const { openModal } = useModal();
  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(1);
  const { barraRef, sentinelaRef, esconder: esconderBarra } = useBarraGrudada();

  const lista = useMemo(() => (Array.isArray(listaDesejos) ? listaDesejos : []), [listaDesejos]);

  const filtrados = useMemo(() => {
    const termo = semAcento(busca).trim();
    if (!termo) return lista;
    return lista.filter((i) => semAcento(i.titulo).includes(termo) || semAcento(autorDe(i)).includes(termo));
  }, [lista, busca]);

  // Buscar volta à primeira página: a página 3 de uma busca com cinco
  // resultados é uma tela vazia.
  useEffect(() => {
    setPagina(1);
  }, [busca]);

  // Tirar o último livro de uma página não pode deixar a pessoa numa página
  // que deixou de existir — o `min` a devolve para a última que sobrou.
  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const inicio = (paginaAtual - 1) * POR_PAGINA;
  const itensPagina = filtrados.slice(inicio, inicio + POR_PAGINA);

  const somaSalva = lista.reduce((s, i) => s + precoDe(i), 0);

  const noCarrinho = (item) =>
    Array.isArray(carrinho) &&
    carrinho.some((c) => String(c.id) === String(item.id) && (c.formato === "fisico" ? "fisico" : "ebook") === formatoDe(item));

  const adicionarAoCarrinho = (item) => {
    if (precoDe(item) <= 0) {
      message.error("Este item não está disponível para compra direta.");
      return;
    }
    // A lista salva já guarda nota e autor; repassá-los evita que o item
    // perca as estrelas ao mudar de lugar (aqui elas aparecem, no carrinho não).
    const cartItem = {
      id: item.id,
      titulo: item.titulo,
      preco: item.preco,
      image: item.image,
      autores: item.autores,
      formato: formatoDe(item),
      quantidade: 1,
      avaliacoes_avg_nota: item.avaliacoes_avg_nota,
      avaliacoes_count: item.avaliacoes_count,
    };
    adicionarItem(cartItem);
    openModal(cartItem);
  };

  return (
    <div>
      {/* ===== O RESUMO =====
          A soma é a única informação AGREGADA da tela — todo o resto é item a
          item —, e agregado que mora numa frase solta passa como legenda do
          título. Num painel ele vira o número que a pessoa veio conferir.

          "SOMAM", e não "custam": o preço de cada livro é o de quando o coração
          foi clicado, e quem confere o de agora é o checkout
          (PagarmeController). Prometer aqui o total de hoje seria prometer um
          preço que esta tela não tem como garantir. */}
      <div className="bloco mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 p-4">
        <div className="min-w-0">
          <h2 className="titulo-display text-[2.1rem] md:text-[2.5rem] flex items-center gap-2">
            <i className="bi bi-heart" /> Minha Lista
          </h2>
          <p className="text-white/50 text-sm mt-1.5">
            {lista.length === 0
              ? "Livros que você salvou para comprar depois."
              : <>{lista.length} {lista.length === 1 ? "livro guardado" : "livros guardados"} para comprar depois</>}
          </p>
        </div>
        {somaSalva > 0 && (
          <p className="shrink-0 leading-tight sm:text-right">
            <span className="block text-[10px] uppercase tracking-wide text-white/40">somam</span>
            <span className="text-lg font-bold text-white">R$ {fmtPreco(somaSalva)}</span>
          </p>
        )}
      </div>

      {lista.length === 0 ? (
        /* O vazio também é uma resposta da tela, e por isso ganhou superfície:
           solto no meio do navy ele parece a página que não terminou de
           carregar. O `px-6 !text-sm` sobre a receita sólida é porque aqui o
           botão está sozinho num painel largo — no card ele divide a linha com
           o de tirar, e lá o corpo menor é que cabe.

           O `!` do `!text-sm` NÃO é enfeite: quando duas classes do mesmo
           utilitário disputam o elemento, o Tailwind 3 as imprime em ordem
           alfabética, e o `text-xs` da receita sai DEPOIS do `text-sm`. Sem o
           `!` o botão fica nos 12px do card. O `px-6` não precisa dele, porque
           `px-6` já sai depois de `px-3.5`. Vale também para o "Limpar busca"
           logo abaixo. */
        <div className="bloco flex flex-col items-center justify-center p-8 text-center">
          <i className="bi bi-heart text-5xl text-white/30 mb-4" />
          <h3 className="text-lg font-bold">Sua lista está vazia</h3>
          <p className="text-white/50 text-sm mt-1 max-w-md leading-relaxed">
            Toque no coração de um livro para guardá-lo aqui e comprar quando quiser.
          </p>
          <Link to={main + "livros"} className={ACAO_SOLIDA + " mt-6 px-6 !text-sm"}>
            <i className="bi bi-search" /> Explorar o catálogo
          </Link>
        </div>
      ) : (
        <>
          {/* A âncora da paginação é a sentinela, e não a barra: a barra é
              `sticky`, e um `scrollIntoView` nela grudada não rola nada. */}
          <div id="lista-topo" ref={sentinelaRef} aria-hidden="true" className="h-0 scroll-mt-24 md:scroll-mt-[280px]" />

          {lista.length >= BUSCA_A_PARTIR_DE && (
            <div
              ref={barraRef}
              className={"mb-4 md:sticky md:z-20 md:-mx-4 md:px-4 md:py-3 md:bg-insigne-bg " +
                         "md:top-[calc(var(--topo-perfil,0px)_+_var(--altura-cabecalho-perfil,0px))] " +
                         CLASSES_SUMICO + " " +
                         (esconderBarra ? CLASSES_ESCONDIDA : "")}
            >
              {/* O CAMPO É O DO SITE (campoCls), e não mais uma cópia com
                  medidas próprias. Duas consequências, as duas de propósito: o
                  foco passa a ser o anel azul de Formulario.js (o laranja é a
                  cor de AÇÃO, e campo com foco laranja lê como botão) e o
                  respiro passa a ser o mesmo dos campos do perfil e do checkout.

                  O `!` do `!pl-11` NÃO é enfeite: campoCls traz `px-4`, e `px-4`
                  é uma das classes que o Bootstrap declara com !important
                  (1,5rem). Sem o `!`, o pedido de 44px à esquerda é ignorado, o
                  texto começa aos 24px e passa por baixo da lupa. */}
              <div className="relative">
                <i className="bi bi-search absolute left-4 top-1/2 -translate-y-1/2 text-sm text-white/40 pointer-events-none" />
                <input
                  type="search"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar por título ou autor..."
                  aria-label="Buscar na minha lista"
                  className={campoCls + " !pl-11"}
                />
              </div>
            </div>
          )}

          {filtrados.length === 0 ? (
            <div className="bloco flex flex-col items-center justify-center p-8 text-center">
              <i className="bi bi-search text-4xl text-white/25 mb-3" />
              <p className="font-semibold">Nada na sua lista com "{busca.trim()}"</p>
              <button
                type="button"
                onClick={() => setBusca("")}
                className={ACAO_SOLIDA + " mt-6 px-6 !text-sm"}
              >
                Limpar busca
              </button>
            </div>
          ) : (
            <>
              {totalPaginas > 1 && (
                <p className="text-xs text-white/45 mb-3">
                  Mostrando {inicio + 1}–{Math.min(inicio + POR_PAGINA, filtrados.length)} de {filtrados.length}
                </p>
              )}

              {/* `gap-3.5`, e não `gap-3` nem `gap-4`: as duas escalas inteiras
                  existem no Bootstrap com !important, e o vão da grade viraria
                  16px ou 24px sem ninguém ter pedido.

                  VOLTA A UMA COLUNA EM `md`, e não é descuido: é em 768px que o
                  menu do perfil (220px, mais 32px de vão) entra ao lado, e esta
                  aba fica MAIS ESTREITA do que era em 640px. Com duas colunas
                  ali sobravam uns 100px para o texto ao lado da capa, e a
                  fileira Adicionar + lixeira, que não encolhe abaixo de uns
                  150px, vazava pela borda do card até perto de 900px — a
                  largura dos tablets em pé. As duas colunas voltam em `lg`,
                  onde cabem com folga. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3.5">
                {itensPagina.map((item) => (
                  <CardDaLista
                    key={`${item.id}-${formatoDe(item)}`}
                    item={item}
                    noCarrinho={noCarrinho(item)}
                    onAdicionar={() => adicionarAoCarrinho(item)}
                    onTirar={() => removerDesejo(item.id, item.formato)}
                  />
                ))}
              </div>

              <Pagination
                pagina={paginaAtual}
                totalPaginas={totalPaginas}
                onChange={setPagina}
                rolarAte="lista-topo"
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Um livro da lista: capa, texto e as duas ações no mesmo cartão.
 *
 * As ações ficam no RODAPÉ do texto, e não numa coluna à direita: é a coluna
 * à direita que obrigava cada linha a ter a largura do perfil inteiro.
 *
 * E desde 11/09/2026 elas ocupam a LINHA INTEIRA do rodapé, em vez de se
 * espremerem no canto com o preço do outro lado: aquele arranjo deixava um vão
 * no meio do rodapé que só existia porque o card tinha nascido de uma linha
 * larga. Esticado, o botão de comprar também ganha o alvo de toque que um botão
 * ao lado de um de apagar precisa ter.
 */
const CardDaLista = ({ item, noCarrinho, onAdicionar, onTirar }) => {
  const preco = precoDe(item);
  const autor = autorDe(item);
  const impresso = formatoDe(item) === "fisico";

  return (
    <article className="cartao flex gap-3.5 p-3.5">
      {/* 80x120 — a proporção 2:3 de capa de livro. Com os 64x96 de antes a
          capa era menor que o bloco de texto ao lado dela nas três larguras, e
          num card de compra a capa é o que a pessoa reconhece primeiro. */}
      <Link
        to={main + "livros/" + item.id}
        className="w-20 h-[7.5rem] shrink-0 overflow-hidden rounded-card bg-insigne-navy-deep flex items-center justify-center"
      >
        {item.image ? (
          <img
            src={`${API_URL}public/${item.image}`}
            alt={item.titulo}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <i className="bi bi-book text-white/30" />
        )}
      </Link>

      <div className="flex min-w-0 flex-1 flex-col">
        <Link
          to={main + "livros/" + item.id}
          className="line-clamp-2 text-sm font-bold leading-snug text-white hover:text-insigne-orange transition-colors"
        >
          {item.titulo}
        </Link>
        {autor && <p className="mt-0.5 truncate text-xs text-white/55" title={autor}>{autor}</p>}
        {/* A nota só aparece se o item foi salvo DEPOIS de 19/08/2026, quando
            o coração passou a guardar os campos de avaliação junto. Para os
            antigos o campo não existe, e "sem avaliações" seria afirmar algo
            que não sabemos. */}
        {item.avaliacoes_count != null && (
          <NotaLivro livro={item} size="text-[10px]" compacto className="mt-1" />
        )}

        <div className="mt-auto pt-2.5">
          <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 leading-tight">
            {preco > 0 && <span className="text-sm font-bold text-white">R$ {fmtPreco(preco)}</span>}
            <span className="text-[10px] uppercase tracking-wide text-white/40">{impresso ? "Impresso" : "E-book"}</span>
          </p>

          {/* "No carrinho" tem a MESMA largura e a mesma forma do botão que ele
              substitui: se encolhesse, o botão de tirar escorregaria para o
              lado a cada livro já adicionado, e o dedo acertaria a lixeira onde
              antes havia o "Adicionar". */}
          <div className="mt-2.5 flex items-center gap-2.5">
            {noCarrinho ? (
              <span className={ACAO_CONTORNO + " flex-1 bg-insigne-orange/10 text-insigne-orange"}>
                <i className="bi bi-cart-check" /> No carrinho
              </span>
            ) : (
              <button
                type="button"
                onClick={onAdicionar}
                disabled={preco <= 0}
                className={ACAO_SOLIDA + " flex-1"}
              >
                <i className="uil uil-shopping-cart" /> Adicionar
              </button>
            )}
            {/* Quadrado de 40px: só o ícone, sem o respiro lateral da receita.
                O `!` do `!px-0` é porque o `px-3.5` dela é impresso DEPOIS na
                folha do Tailwind, e sem ele o ícone fica espremido em 12px. */}
            <button
              type="button"
              onClick={onTirar}
              title="Tirar da lista"
              aria-label={`Tirar "${item.titulo}" da lista`}
              className={ACAO_CONTORNO + " w-10 shrink-0 !px-0 text-white/55 hover:bg-red-500/10 hover:text-red-400"}
            >
              <i className="bi bi-trash" />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
};

export default MinhaLista;
